import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Navbar from "../component/Navbar/NavbarCommon";
import Footer from "../component/Footer/FooterCommon";
import { api } from "../api";
import { API_URL, ASSETS_URL } from "../utils/config";
import { toast } from "react-toastify";
import moment from "moment";
import ArrowButton from "../component/Common/ArrowButton";

interface Blog {
  _id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  coverImage: string;
  category: string;
  tags: string[];
  status: string;
  readingTime: number;
  featured: boolean;
  publishedAt: string;
  createdAt: string;
  author?: {
    name: string;
    avatar: string;
  };
}

export default function BlogDetails() {
  const { id } = useParams();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogDetails();
  }, [id]);

  const fetchBlogDetails = async () => {
    try {
      const { data, error } = await api.get(`${API_URL.blogs}/${id}`);
      if (error) throw new Error(error.message);
      setBlog((data as any)?.data);
    } catch (err: any) {
      toast.error(err.message || "Failed to fetch blog");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500 text-lg font-poppins">
        Loading...
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500 text-lg font-poppins">
        Blog not found.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-poppins overflow-x-hidden">
      <Navbar />

      <div className="pt-28 pb-12 px-4 sm:px-6 lg:px-20 sm:mt-9 mb-24 overflow-hidden">
        <ArrowButton />

        {/* Blog Article */}
        <article className="max-w-4xl mx-auto overflow-hidden">
          {/* Cover Image */}
          {blog.coverImage && (
            <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-xl mb-8">
              <img
                src={`${ASSETS_URL}${blog.coverImage}`}
                alt={blog.title}
                className="w-full h-64 sm:h-80 md:h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            </div>
          )}

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            {blog.category && (
              <span className="px-3 py-1.5 bg-navgreen text-white text-sm rounded-full font-medium">
                {blog.category}
              </span>
            )}
            <span className="text-gray-500 text-sm sm:text-base">
              {moment(blog.publishedAt || blog.createdAt).format("MMMM D, YYYY")}
            </span>
            <span className="text-gray-300">|</span>
            <span className="text-gray-500 text-sm sm:text-base">
              {blog.readingTime || 1} min read
            </span>
          </div>

          {/* Title */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-deepTeal mb-6 leading-tight">
            {blog.title}
          </h1>

          {/* Author Info */}
          {blog.author?.name && (
            <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-200">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-navgreen/20 flex items-center justify-center text-navgreen font-semibold text-lg">
                {blog.author.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-medium text-deepTeal">{blog.author.name}</p>
                <p className="text-gray-500 text-sm">Author</p>
              </div>
            </div>
          )}

          {/* Blog Content */}
          <div
            className="prose prose-lg max-w-none text-gray-700 leading-relaxed overflow-hidden break-words
              prose-headings:text-deepTeal prose-headings:font-semibold
              prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl
              prose-p:mb-4 prose-p:text-base sm:prose-p:text-lg prose-p:break-words
              prose-a:text-navgreen prose-a:no-underline hover:prose-a:underline prose-a:break-all
              prose-img:rounded-xl prose-img:shadow-lg prose-img:max-w-full prose-img:h-auto
              prose-blockquote:border-l-4 prose-blockquote:border-navgreen prose-blockquote:bg-gray-50 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:italic
              prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm prose-code:break-all
              prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-xl prose-pre:overflow-x-auto prose-pre:max-w-full
              prose-ul:list-disc prose-ol:list-decimal
              prose-li:mb-2
              [&_img]:max-w-full [&_img]:h-auto [&_iframe]:max-w-full [&_video]:max-w-full [&_table]:max-w-full [&_table]:overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />

          {/* Tags */}
          {blog.tags && blog.tags.length > 0 && (
            <div className="mt-10 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-500 mb-3">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {blog.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-gray-200 transition-colors"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Published Info */}
          <div className="mt-10 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500 italic">
              Published on {moment(blog.publishedAt || blog.createdAt).format("dddd, MMMM D, YYYY")}
            </p>
          </div>
        </article>
      </div>

      <Footer />
    </div>
  );
}
